import moment from "moment";

import {
  DEFAULT_GRAPHS_BEST_DAYS,
  DEFAULT_GRAPHS_BOOKMARKS,
  DEFAULT_GRAPHS_VALUES,
  FEATURES_DEFAULT_WEIGHTS,
  RADIO_SWITCHER_VALUES,
  STOCKPILES_SEARCH_TIME_START,
  STOCKPILES_TABLE_FEATURES,
} from "../constants";
import { keysToUpperCase } from "../utils/create-table-data";
import {
  SET_DATA,
  SET_TABLE_INPUTS,
  SET_HIGHLIGHTED_COLS,
  SET_HIGHLIGHTED_ROWS,
  SET_EMPTY_FORECAST,
  SET_FORECASTED_STOCKPILE,
  SET_ACTIVE_FORECAST,
  SET_FILTERED_HIGHLIGHTED_STOCKPILES,
  SET_USER_INPUT_DATA,
} from "./actionTypes";

type actionType = {
  type: string;
  payload: any;
};

export interface IMatchState {
  requestId: string;
  ioSwitcherValue: string;
  startDate: string;
  startDatePrevious: string;
  endDate: string;
  endDatePrevious: string;
  stockpileName: string;
  forecastNotes: string;
  forecastNotesPrevious: string;
  forecastedStockpile: string;
  forecastId: string;
  forecastIdPrevious: string;
  forecastRequestId: string;
  forecastRequestIdPrevious: string;
  showStockpileData: boolean;
  showRequestsModal: boolean;
  loadingStockpilesData: boolean;
  readyForSearch: boolean;
  forecastReadyForSave: boolean;
  hideMatchingTable: boolean;
  highlightedCols: string[];
  highlightedRows: string[];
  stockpiles: any;
  userInputError: any;
  avgRow: any;
  graphSensor: any;
  graphsBestDays: any;
  graphsBookmarks: any;
  tableInputs: any;
  tableInputsPrevious: any;
  tableWeights: any;
  tableWeightsPrevious: any;
  inputErrors: any;
  sensorsList: any;
  forecastTableRows: any;
  forecastTableRowsPrevious: string[];
  forecastInputErrors: any;
  forecastAverageData: any;
  showGraphExportModal: boolean;
  selectedSensorList: any;
  exportBookmarks: boolean;
  exportBestDays: boolean;
}

export const initialMatchState: IMatchState = {
  requestId: "",
  ioSwitcherValue: RADIO_SWITCHER_VALUES.INPUT,
  startDate: STOCKPILES_SEARCH_TIME_START,
  startDatePrevious: "",
  endDate: moment().format("YYYY-MM-DD"),
  endDatePrevious: "",
  stockpileName: "",
  forecastNotes: "",
  forecastNotesPrevious: "",
  forecastedStockpile: "",
  forecastId: "",
  forecastIdPrevious: "",
  forecastRequestId: "",
  forecastRequestIdPrevious: "",
  showStockpileData: false,
  showRequestsModal: false,
  loadingStockpilesData: false,
  readyForSearch: false,
  forecastReadyForSave: false,
  hideMatchingTable: false,
  highlightedCols: [],
  highlightedRows: [],
  stockpiles: null,
  userInputError: null,
  avgRow: {},
  tableInputs: {},
  tableInputsPrevious: {},
  tableWeights: FEATURES_DEFAULT_WEIGHTS,
  tableWeightsPrevious: {},
  inputErrors: {},
  sensorsList: [],
  graphSensor: DEFAULT_GRAPHS_VALUES,
  graphsBestDays: DEFAULT_GRAPHS_BEST_DAYS,
  graphsBookmarks: DEFAULT_GRAPHS_BOOKMARKS,
  forecastTableRows: [{ id: Date.now() }],
  forecastTableRowsPrevious: [],
  forecastInputErrors: {},
  forecastAverageData: {},
  showGraphExportModal: false,
  selectedSensorList: [],
  exportBookmarks: true,
  exportBestDays: true,
};

// todo refactor: move logic to reducer, split reducer
export const matchReducer = (
  state: IMatchState,
  action: actionType
): IMatchState => {
  switch (action.type) {
    case SET_DATA:
      return { ...state, ...action.payload };
    case SET_TABLE_INPUTS: {
      const znPercent =
        action.payload[STOCKPILES_TABLE_FEATURES.ZN_PERCENT.toUpperCase()] ||
        state.tableInputs[STOCKPILES_TABLE_FEATURES.ZN_PERCENT.toUpperCase()];
      const fePercent =
        action.payload[STOCKPILES_TABLE_FEATURES.FE_PERCENT.toUpperCase()] ||
        state.tableInputs[STOCKPILES_TABLE_FEATURES.FE_PERCENT.toUpperCase()];
      const pbPercent =
        action.payload[STOCKPILES_TABLE_FEATURES.PB_PERCENT.toUpperCase()] ||
        state.tableInputs[STOCKPILES_TABLE_FEATURES.PB_PERCENT.toUpperCase()];
      const spbPercent =
        action.payload[STOCKPILES_TABLE_FEATURES.SPB_PERCENT.toUpperCase()] ||
        state.tableInputs[STOCKPILES_TABLE_FEATURES.SPB_PERCENT.toUpperCase()];

      const znFeRatio = znPercent && fePercent ? znPercent / fePercent : 0;
      const znPbRatio = znPercent && pbPercent ? znPercent / pbPercent : 0;
      const pbFeRatio = pbPercent && fePercent ? pbPercent / fePercent : 0;
      const rpbRatio = pbPercent && spbPercent ? spbPercent / pbPercent : 0;

      const ratios = {
        [STOCKPILES_TABLE_FEATURES.ZNFE_RATIO.toUpperCase()]:
          +Number(znFeRatio).toFixed(2),
        [STOCKPILES_TABLE_FEATURES.ZNPB_RATIO.toUpperCase()]:
          +Number(znPbRatio).toFixed(2),
        [STOCKPILES_TABLE_FEATURES.PBFE_RATIO.toUpperCase()]:
          +Number(pbFeRatio).toFixed(2),
        [STOCKPILES_TABLE_FEATURES.RPB_RATIO.toUpperCase()]:
          +Number(rpbRatio).toFixed(2),
      };

      return { ...state, ...{ tableInputs: { ...action.payload, ...ratios } } };
    }
    case SET_HIGHLIGHTED_COLS:
      return { ...state, ...{ highlightedCols: action.payload } };
    case SET_HIGHLIGHTED_ROWS:
      return { ...state, ...{ highlightedRows: action.payload } };
    case SET_EMPTY_FORECAST:
      return {
        ...state,
        ...{
          forecastTableRows: [{ id: Date.now() }],
          forecastNotes: "",
          forecastId: "",
          forecastRequestId: "",
          forecastInputErrors: {},
        },
      };
    case SET_FORECASTED_STOCKPILE:
      if (state.forecastedStockpile === action.payload) {
        return { ...state, ...{ forecastedStockpile: "" } };
      } else {
        return { ...state, ...{ forecastedStockpile: action.payload } };
      }
    case SET_ACTIVE_FORECAST: {
      return {
        ...state,
        ...{
          forecastTableRows: JSON.parse(action.payload.forecastTableRows),
          forecastTableRowsPrevious: JSON.parse(
            action.payload.forecastTableRows
          ),
          forecastId: action.payload.forecastId,
          forecastIdPrevious: action.payload.forecastId,
          forecastRequestId: action.payload.forecastRequestId,
          forecastRequestIdPrevious: action.payload.forecastRequestId,
          forecastNotes: action.payload.forecastNotes,
          forecastNotesPrevious: action.payload.forecastNotes,
        },
      };
    }
    case SET_FILTERED_HIGHLIGHTED_STOCKPILES: {
      if (state.highlightedCols.length) {
        const newCols: string[] = action.payload[0]
          .map((item) => item.stockpile)
          .filter((item: string) => state.highlightedCols.includes(item));
        return { ...state, ...{ highlightedCols: newCols } };
      }
      return { ...state };
    }
    case SET_USER_INPUT_DATA: {
      const data = action.payload.getUserInputData;

      return {
        ...state,
        ...{
          showStockpileData: true,
          startDate: data.startDate,
          startDatePrevious: data.startDate,
          endDate: data.endDate,
          endDatePrevious: data.endDate,
          stockpileName: data.stockpileName,
          tableWeights: keysToUpperCase(data.tableWeights),
          tableInputsPrevious: keysToUpperCase(data.tableInputs),
          tableWeightsPrevious: keysToUpperCase(data.tableWeights),
        },
      };
    }
    default:
      return { ...state };
  }
};
